import { useNavigate } from "react-router-dom";
import { maskMiddleCharacters } from "../../util/function/function";
import { useUser } from "util/context/user_context";
import moment from "moment";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  red: "#EF4444",
  amber800: "#92400E",
  amber100: "#FEF3C7",
};

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YYYY.MM.DD");
};

function CategoryBadge({ type }) {
  const isInquiry = type === "고장 문의" || type === "고장문의";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 9999,
        fontSize: 11,
        fontWeight: 700,
        background: isInquiry ? C.amber100 : C.greenBg,
        color: isInquiry ? C.amber800 : C.greenDark,
        whiteSpace: "nowrap",
      }}
    >
      {isInquiry ? "고장 문의" : "농사 이야기"}
    </span>
  );
}

function HeartIcon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={active ? C.red : "none"} stroke={active ? C.red : C.sub} strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function CommentIconSvg() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export default function Feed({ story }) {
  const navigate = useNavigate();
  const { userData } = useUser();
  const liked = Array.isArray(story.liked) && story.liked.includes(userData.id);
  const images = Array.isArray(story.imageUrls) ? story.imageUrls : [];
  const firstImage = images[0];

  return (
    <div
      onClick={() => navigate(`/community/${story.docId}`)}
      style={{
        display: "flex",
        gap: 16,
        padding: 20,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.08)";
        e.currentTarget.style.borderColor = C.greenBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = C.line;
      }}
    >
      {/* Thumbnail */}
      {firstImage ? (
        <div
          style={{
            width: 112,
            height: 112,
            borderRadius: 12,
            background: "#F3F4F6",
            flexShrink: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <img
            src={firstImage}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          {images.length > 1 && (
            <span
              style={{
                position: "absolute",
                bottom: 6,
                right: 6,
                background: "rgba(17,24,39,0.75)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 6px",
                borderRadius: 6,
              }}
            >
              +{images.length - 1}
            </span>
          )}
        </div>
      ) : null}

      {/* Body */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <CategoryBadge type={story.type} />
            <span style={{ fontSize: 12, color: C.sub }}>
              {relativeTime(story.createDate)}
            </span>
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {story.title}
          </div>
          <div
            style={{
              fontSize: 14,
              color: C.body,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "pre-wrap",
            }}
          >
            {story.body}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 6,
            borderTop: `1px solid ${C.line}`,
          }}
        >
          <span style={{ fontSize: 13, color: C.sub, fontWeight: 500 }}>
            {maskMiddleCharacters(story.name) || "익명"}
          </span>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                color: liked ? C.red : C.sub,
              }}
            >
              <HeartIcon active={liked} />
              {story.likes ?? 0}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                color: C.sub,
              }}
            >
              <CommentIconSvg />
              {story.commentCount ?? 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
